// pipe: the ERP sidebar sections as DATA — one `{ id, element }` per section, in the default
// order (`DEFAULT_NAV_ORDER`). `ErpNavList` renders this list reordered/filtered by the user's
// `nav.layout.v1` preference; every element is the SAME gated button component as before, so the
// server-driven gates (admin, stations, AI access) run unchanged after the reorder. A section
// whose screen is not built yet has `element: null` and is skipped by the renderer.
import { AuthService } from '@affine/core/modules/cloud';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactElement } from 'react';

import {
  DEFAULT_NAV_ORDER,
  type NavId,
} from '../../modules/erp/nav-layout';
import { AgreementsButton } from './agreements-button';
import { AllDocsButton } from './all-docs-button';
import { AskButton } from './ask-button';
import { CommsButton } from './comms-button';
import { CustomersButton } from './customers-button';
import { DashboardButton } from './dashboard-button';
import { DirectorButton } from './director-button';
import { FinanceButton } from './finance-button';
import { InboundButton } from './inbound-button';
import { IntakeButton } from './intake-button';
import { AppSidebarJournalButton } from './journal-button';
import { ListingsButton } from './listings-button';
import { NotificationButton } from './notification-button';
import { OpsButton } from './ops-button';
import { QmsButton } from './qms-button';
import { RecordsButton } from './records-button';
import { RefiningButton } from './refining-button';
import { ReportsButton } from './reports-button';
import { SalesButton } from './sales-button';
import { SidebarScanButton } from './scan-button';
import { SitesButton } from './sites-button';
import { WorkButton } from './work-button';

/** The notification inbox exists only for a signed-in cloud session (the gate index.tsx held). */
function NotificationWhenSignedIn() {
  const status = useLiveData(useService(AuthService).session.status$);
  return status === 'authenticated' ? <NotificationButton /> : null;
}

export interface ErpNavItem {
  id: NavId;
  element: ReactElement | null;
}

const ELEMENTS: Record<NavId, ReactElement | null> = {
  dashboard: <DashboardButton />,
  'all-docs': <AllDocsButton />,
  records: <RecordsButton />,
  ops: <OpsButton />,
  intake: <IntakeButton />,
  customers: <CustomersButton />,
  sites: <SitesButton />,
  scan: <SidebarScanButton />,
  work: <WorkButton />,
  director: <DirectorButton />,
  qms: <QmsButton />,
  refining: <RefiningButton />,
  sales: <SalesButton />,
  listings: <ListingsButton />,
  comms: <CommsButton />,
  agreements: <AgreementsButton />,
  inbound: <InboundButton />,
  finance: <FinanceButton />,
  reports: <ReportsButton />,
  ask: <AskButton />,
  journal: <AppSidebarJournalButton />,
  notifications: <NotificationWhenSignedIn />,
};

export const ERP_NAV_ITEMS: ReadonlyArray<ErpNavItem> = DEFAULT_NAV_ORDER.map(
  id => ({ id, element: ELEMENTS[id] })
);
